import { type CurrentUser, type User } from '@/models/account-management/User';
import { createApiResource, listWithOrg, saveWithOrg } from '../adminApi';
import { getJwt, HttpError } from '../common';

const BASE_URL = import.meta.env.VITE_API_URL;
const USERS_PATH = 'account-management/users';

export const userApi = {
    ...createApiResource<User, string>(USERS_PATH),
    // Users follow the VIEWED org — list/create scope to it (a super-admin may pass any org).
    list: (organizationId?: string | null): Promise<User[]> => listWithOrg<User>(USERS_PATH, organizationId),
    save: (item: User, organizationId?: string | null): Promise<User | void> => saveWithOrg<User>(USERS_PATH, item, organizationId),
    // Throwing on a refusal is what makes the sign-in gate work, so this is not merely house style.
    //
    // fetch only rejects on a network failure, so a 403 or 404 resolves like any other response. Returning
    // response.json() unconditionally handed back PortalAPI's ProblemDetails body typed as a CurrentUser: a
    // truthy object with no firstName and no permissions. The gate saw a user and let a disabled or deleted
    // account into the app, the provider saw no error so it never revoked the session, and the sign-in screen
    // had nothing to explain. The empty name and the "Access denied" page QA reported were that error payload
    // being rendered as a person (TTD-121).
    getCurrentUser: async (): Promise<CurrentUser> => {
        const response = await fetch(`${BASE_URL}/account-management/users/current`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${getJwt()}`
            }
        });
        // Tolerated because a refusal is the case that matters and it may carry no body at all; the status is
        // what the caller acts on, and HttpError keeps it either way.
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new HttpError(response.status, data);
        return data as CurrentUser;
    }
};